import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { FadeUp, Button } from "../components/primitives";
import { RichText } from "../components/RichText";
import { api } from "../lib/api";
import { useLang } from "../i18n";

const Article = () => {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/articles/${slug}`).then((r) => setArticle(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!article) return;
    const seoTitle = (lang === "es" ? article.seo_title_es : article.seo_title_en)
      || (lang === "es" ? article.title_es : article.title_en);
    const seoDesc = (lang === "es" ? article.seo_desc_es : article.seo_desc_en)
      || (lang === "es" ? article.excerpt_es : article.excerpt_en);
    const prevTitle = document.title;
    document.title = `${seoTitle} | WANM`;
    let meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute("content") : null;
    if (meta && seoDesc) meta.setAttribute("content", seoDesc);
    return () => { document.title = prevTitle; if (meta && prevDesc != null) meta.setAttribute("content", prevDesc); };
  }, [article, lang]);

  if (notFound) return (
    <div className="pt-48 pb-32 text-center">
      <p className="text-2xl font-black text-wanm-dark">404</p>
      <Link to="/insights" className="mt-4 inline-block text-wanm-red font-bold">{t.insights.back}</Link>
    </div>
  );
  if (!article) return <div className="pt-48 pb-32 text-center text-wanm-dark/40">…</div>;

  const title = (lang === "es" ? article.title_es : article.title_en) || article.title_en;
  const body = (lang === "es" ? article.body_es : article.body_en) || article.body_en || article.excerpt_en;
  const date = article.publish_date || (article.created_at || "").slice(0, 10);

  return (
    <article className="overflow-hidden">
      <section className="pt-40 pb-16 md:pt-48 bg-wanm-offwhite grain">
        <div className="relative z-10 mx-auto max-w-4xl px-5 md:px-10">
          <Link to="/insights" className="inline-flex items-center gap-2 text-wanm-dark/60 hover:text-wanm-red font-bold mb-8 transition-colors" data-testid="back-link">
            <ArrowLeft size={18} /> {t.insights.back}
          </Link>
          <span className="text-sm font-black uppercase tracking-widest text-wanm-red">{article.category}</span>
          <h1 className="mt-4 text-4xl md:text-6xl font-black tracking-tighter text-wanm-dark leading-[0.95]" data-testid="article-title">{title}</h1>
          <p className="mt-6 text-wanm-dark/50 font-bold">{article.author}{date ? ` · ${date}` : ""}</p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="mx-auto max-w-3xl px-5 md:px-10">
          {article.cover_image && (
            <FadeUp><img src={article.cover_image} alt="" className="w-full rounded-2xl aspect-video object-cover mb-12" /></FadeUp>
          )}
          <FadeUp delay={0.1}>
            <RichText>{body}</RichText>
          </FadeUp>
          <div className="mt-16 pt-10 border-t border-wanm-dark/10">
            <Button variant="primary" to="/contact">{t.cta.work}</Button>
          </div>
        </div>
      </section>
    </article>
  );
};

export default Article;
